import React, { useRef, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useSvgStore, canSplitSvgContent, downloadSvgContent } from '../../store/svgStore';
import { SvgCanvasData, SvgNodeElement, SvgGroupElement, SvgCanvasElement } from '../../types/svgTypes';
import { injectAnimationToSvg } from '../../utils/svgAnimationUtils';
import { prepareSvgForEditMode, rebuildSvgContent } from '../../utils/svgParser';
import ImportSvgModal from './ImportSvgModal';
import CanvasMinimap from './CanvasMinimap';
import ConfirmModal from '../common/ConfirmModal';
import { SvgExtractResult } from '../../utils/svgImportUtils';
import { showToast } from '../common/Toast';

interface SelectionBox {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}
const SNAP_GRID = 10;
const MAX_UNDO_STACK_SIZE = 50;
/** 方向键普通步距 */
const ARROW_STEP = 1;
/** 方向键 Shift 步距 */
const ARROW_SHIFT_STEP = 10;

/** 深拷贝 canvas（与 store 中保持一致） */
const cloneCanvas = (canvas: SvgCanvasData): SvgCanvasData =>
  JSON.parse(JSON.stringify(canvas));

/** ★ 判断是否为多选修饰键（Ctrl 或 Shift） */
const isMultiSelectKey = (e: React.MouseEvent | MouseEvent): boolean =>
  e.ctrlKey || e.metaKey || e.shiftKey;

interface SvgCanvasDesignerProps {
  /** 左侧面板是否收起 */
  leftCollapsed?: boolean;
  /** 切换左侧面板展开/收起 */
  onToggleLeftPanel?: () => void;
  rightCollapsed?: boolean;
  onToggleRightPanel?: () => void;
}

const SvgCanvasDesigner: React.FC<SvgCanvasDesignerProps> = ({
  leftCollapsed = false,
  onToggleLeftPanel,
  rightCollapsed = false,
  onToggleRightPanel,
}) => {
  const canvas = useSvgStore((state) => state.canvas);

  const selectedState = useSvgStore((state) => state.selectedState);
  const setSelectedIds = useSvgStore((state) => state.setSelectedIds);
  const setActiveElement = useSvgStore((state) => state.setActiveElement);
  const updateElements = useSvgStore((state) => state.updateElements);
  const removeElements = useSvgStore((state) => state.removeElements);
  const addElement = useSvgStore((state) => state.addElement);
  const exportSvgFile = useSvgStore((state) => state.exportSvgFile);
  const addAsset = useSvgStore((state) => state.addAsset);

  const isSubElementEditMode = useSvgStore((state) => state.isSubElementEditMode);
  const editingNodeId = useSvgStore((state) => state.editingNodeId);
  const selectedSubElementId = useSvgStore((state) => state.selectedSubElementId);
  const enterSubElementEditMode = useSvgStore((state) => state.enterSubElementEditMode);
  const exitSubElementEditMode = useSvgStore((state) => state.exitSubElementEditMode);
  const selectSubElement = useSvgStore((state) => state.selectSubElement);
  const parseNodeSubElements = useSvgStore((state) => state.parseNodeSubElements);

  // ★ 新增：视口和组合相关
  const viewport = useSvgStore((state) => state.viewport);
  const zoomAtPoint = useSvgStore((state) => state.zoomAtPoint);
  const resetViewport = useSvgStore((state) => state.resetViewport);
  const scaleSelectedElements = useSvgStore((state) => state.scaleSelectedElements);
  const groupElements = useSvgStore((state) => state.groupElements);
  const ungroupElements = useSvgStore((state) => state.ungroupElements);
  // ★ 新增：拆分功能
  const splitElement = useSvgStore((state) => state.splitElement);
  const clearCanvas = useSvgStore((state) => state.clearCanvas);
  // ★ 新增：视口平移
  const setPan = useSvgStore((state) => state.setPan);

  const [interactionMode, setInteractionMode] = useState<'IDLE' | 'SELECTING' | 'DRAGGING' | 'PANNING'>('IDLE');
  // ★ 新增：当前工具（默认选择工具，可切换到抓手）
  const [activeTool, setActiveTool] = useState<'select' | 'hand'>('select');
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [selectedBBoxes, setSelectedBBoxes] = useState<Record<string, DOMRect>>({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ★ 新增：抓手拖拽 ref
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const svgRef = useRef<SVGSVGElement>(null);
  
  // 记录拖拽开始时的 BBox，拖拽过程中不重新计算
  const dragStartBBoxesRef = useRef<Record<string, DOMRect>>({});
  /** ★ 拖拽前的 canvas 快照（用于在 mouseUp 时推入 undoStack） */
  const dragUndoSnapshotRef = useRef<SvgCanvasData | null>(null);

   // ★ 修复性能：只依赖选中元素的 svgContent，不依赖整个 canvas
  const selectedSvgContent = useMemo(() => {
    if (selectedState.selectedIds.length !== 1 || !canvas) return null;
    const el = canvas.elements.find(e => e.id === selectedState.selectedIds[0]);
    if (!el || el.type !== 'svg') return null;
    return (el as SvgNodeElement).svgContent;
  }, [selectedState.selectedIds, canvas?.elements]);

  const canSplitSelected = useMemo(() => {
    if (!selectedSvgContent) return false;
    return canSplitSvgContent(selectedSvgContent);
  }, [selectedSvgContent]);

  /**
   * ★ 重置视口：将所有元素居中到画布可见区域
   * 考虑容器实际像素尺寸（左右面板展开时容器更窄）
   */
  const handleResetViewport = useCallback(() => {
    if (!canvas || canvas.elements.length === 0) {
      resetViewport();
      return;
    }

    const DEFAULT_SIZE = 50;
    const PADDING = 40;

    // 计算所有顶层元素的包围盒
    const groupChildIds = new Set<string>();
    canvas.elements.forEach(el => {
      if (el.type === 'group') {
        (el as SvgGroupElement).children.forEach(id => groupChildIds.add(id));
      }
    });

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const el of canvas.elements) {
      if (groupChildIds.has(el.id)) continue;
      const x = el.type === 'svg' ? (el as SvgNodeElement).x : (el as SvgGroupElement).x;
      const y = el.type === 'svg' ? (el as SvgNodeElement).y : (el as SvgGroupElement).y;
      const scale = el.type === 'svg' ? ((el as SvgNodeElement).scale ?? 1) : ((el as SvgGroupElement).scale ?? 1);

      const domEl = (
        document.getElementById(`node-content-${el.id}`) ??
        document.getElementById(`group-container-${el.id}`)
      ) as SVGGraphicsElement | null;

      let w = DEFAULT_SIZE, h = DEFAULT_SIZE, bx = 0, by = 0;
      if (domEl && typeof domEl.getBBox === 'function') {
        try {
          const bbox = domEl.getBBox();
          w = bbox.width || DEFAULT_SIZE;
          h = bbox.height || DEFAULT_SIZE;
          bx = bbox.x;
          by = bbox.y;
        } catch { /* fallback */ }
      }

      minX = Math.min(minX, x + bx * scale);
      minY = Math.min(minY, y + by * scale);
      maxX = Math.max(maxX, x + bx * scale + w * scale);
      maxY = Math.max(maxY, y + by * scale + h * scale);
    }

    if (!isFinite(minX)) {
      resetViewport();
      return;
    }

    // 获取容器实际像素尺寸（已反映左右面板占用）
    const wrapper = wrapperRef.current;
    const containerW = wrapper ? wrapper.clientWidth : canvas.width;
    const containerH = wrapper ? wrapper.clientHeight : canvas.height;

    const contentW = maxX - minX;
    const contentH = maxY - minY;
    const contentCX = (minX + maxX) / 2;
    const contentCY = (minY + maxY) / 2;

    // 计算 zoom：让内容 + 留白刚好适配容器
    const zoomX = containerW / (contentW + PADDING * 2);
    const zoomY = containerH / (contentH + PADDING * 2);
    const fitZoom = Math.round(Math.min(zoomX, zoomY, 1) * 100) / 100; // 不超过 1
    const zoom = Math.max(0.1, fitZoom);

    // 计算 pan：让内容中心 = 可见区域中心
    // 可见区域 SVG 坐标宽度 = containerW / zoom
    const panX = contentCX - containerW / (2 * zoom);
    const panY = contentCY - containerH / (2 * zoom);

    resetViewport({ zoom, panX, panY });
  }, [canvas, resetViewport]);

  // ★ 计算 viewBox
  const computedViewBox = canvas
    ? `${viewport.panX} ${viewport.panY} ${canvas.width / viewport.zoom} ${canvas.height / viewport.zoom}`
    : '0 0 1200 800';

  /**
   * 计算选中元素的 BBox
   * 只在选中状态变化且不在拖拽时调用
   */
  const updateBBoxes = useCallback(() => {
    const newBBoxes: Record<string, DOMRect> = {};
    if (selectedState.selectedIds.length > 0) {
      selectedState.selectedIds.forEach(id => {
        // ★ 修复：同时查找 node-content 和 group-container
        const el = (
          document.getElementById(`node-content-${id}`) ??
          document.getElementById(`group-container-${id}`)
        ) as SVGGraphicsElement | null;
        if (el && typeof el.getBBox === 'function') {
          try {
            const bbox = el.getBBox();
            const adjustedBBox = new DOMRect(
              bbox.x,
              bbox.y,
              bbox.width === 0 ? 50 : bbox.width,
              bbox.height === 0 ? 50 : bbox.height
            );
            newBBoxes[id] = adjustedBBox;
          } catch (e) {
            console.warn('Cannot retrieve BBox for', id);
          }
        }
      });
    }
    setSelectedBBoxes(newBBoxes);
  }, [selectedState.selectedIds]);

  // 只在选中 ID 变化时更新 BBox，不依赖 elements
  useLayoutEffect(() => {
    // 如果正在拖拽，不更新 BBox
    if (interactionMode === 'DRAGGING') {
      return;
    }
    
    // 延迟一帧确保 DOM 已更新
    requestAnimationFrame(() => {
      updateBBoxes();
    });
  }, [selectedState.selectedIds, interactionMode, updateBBoxes]);

  const handleDeleteSelected = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedState.selectedIds.length > 0) {
      removeElements(selectedState.selectedIds);
      setSelectedIds([]);
      setActiveElement(undefined);
    }
  };

  /**
   * ★ 需求4: group 的 X 按钮改为解组（保留子元素在当前位置）
   */
  const handleUngroupSelected = (e: React.MouseEvent, groupId: string) => {
    e.stopPropagation();
    ungroupElements(groupId);
  };

  /**
   * ★ 新增：递归将 canvas 元素序列化为 SVG 字符串片段
   * 支持 svg 节点和嵌套 group，确保导出/添加素材库时不丢失嵌套组合的内容
   */
  const serializeElementToSvgFragment = useCallback((elementId: string): string => {
    if (!canvas) return '';

    const el = canvas.elements.find(e => e.id === elementId);
    if (!el) return '';

    if (el.type === 'svg') {
      const node = el as SvgNodeElement;
      const tx = node.x ?? 0;
      const ty = node.y ?? 0;
      const scale = node.scale ?? 1;
      const rotation = node.rotation ?? 0;
      const opacity = node.opacity ?? 1;
      const transforms: string[] = [];
      if (tx !== 0 || ty !== 0) transforms.push(`translate(${tx},${ty})`);
      if (scale !== 1) transforms.push(`scale(${scale})`);
      if (rotation !== 0) transforms.push(`rotate(${rotation})`);
      const transformAttr = transforms.length > 0 ? ` transform="${transforms.join(' ')}"` : '';
      const opacityAttr = opacity !== 1 ? ` opacity="${opacity}"` : '';
      return `<g${transformAttr}${opacityAttr}>${node.svgContent}</g>`;
    }

    if (el.type === 'group') {
      const group = el as SvgGroupElement;
      const tx = group.x ?? 0;
      const ty = group.y ?? 0;
      const scale = group.scale ?? 1;
      const rotation = group.rotation ?? 0;
      const opacity = group.opacity ?? 1;
      const transforms: string[] = [];
      if (tx !== 0 || ty !== 0) transforms.push(`translate(${tx},${ty})`);
      if (scale !== 1) transforms.push(`scale(${scale})`);
      if (rotation !== 0) transforms.push(`rotate(${rotation})`);
      const transformAttr = transforms.length > 0 ? ` transform="${transforms.join(' ')}"` : '';
      const opacityAttr = opacity !== 1 ? ` opacity="${opacity}"` : '';

      // ★ 递归序列化所有子元素（包括嵌套 group）
      const childSvgs = group.children
        .map(childId => serializeElementToSvgFragment(childId))
        .join('');

      return `<g${transformAttr}${opacityAttr}>${childSvgs}</g>`;
    }

    return '';
  }, [canvas]);

  /**
   * ★ 修复：添加选中元素到素材库
   * 改进：使用递归序列化，支持嵌套 group
   */
  const handleAddToAssetLibrary = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    if (!canvas) return;

    const el = canvas.elements.find(e => e.id === elementId);
    if (!el) return;

    let svgCode = '';
    let name = el.name || 'SVG';

    if (el.type === 'svg') {
      const node = el as SvgNodeElement;
      const bbox = selectedBBoxes[elementId];
      if (bbox && bbox.width > 0 && bbox.height > 0) {
        const w = Math.round(bbox.width);
        const h = Math.round(bbox.height);
        svgCode = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}">${node.svgContent}</svg>`;
      } else {
        svgCode = node.svgContent;
      }
    } else if (el.type === 'group') {
      const group = el as SvgGroupElement;
      // ★ 修复：递归序列化所有子元素（包括嵌套 group）
      const childSvgs = group.children
        .map(childId => serializeElementToSvgFragment(childId))
        .join('');

      const bbox = selectedBBoxes[elementId];
      if (bbox && bbox.width > 0 && bbox.height > 0) {
        const w = Math.round(bbox.width);
        const h = Math.round(bbox.height);
        svgCode = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}"><g>${childSvgs}</g></svg>`;
      } else {
        svgCode = `<svg xmlns="http://www.w3.org/2000/svg"><g>${childSvgs}</g></svg>`;
      }
      name = group.name || `组合_${group.children.length}`;
    }

    if (!svgCode) return;

    addAsset({
      name,
      svg_code: svgCode,
      is_public: false,
    }).then(() => {
      showToast(`「${name}」已添加到素材库`, 'success');
    }).catch(err => {
      showToast('添加到素材库失败', 'error');
    });
  };

  /**
   * ★ 修复：导出选中元素为 SVG 文件
   * 改进：使用递归序列化，支持嵌套 group
   */
  const handleExportSelectedSvg = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    if (!canvas) return;

    const el = canvas.elements.find(e => e.id === elementId);
    if (!el) return;

    let svgCode = '';
    let name = el.name || 'export';
    let exportWidth: number | undefined;
    let exportHeight: number | undefined;
    let viewBox: { x: number; y: number; width: number; height: number } | undefined;

    if (el.type === 'svg') {
      const node = el as SvgNodeElement;
      svgCode = node.svgContent;
      const bbox = selectedBBoxes[elementId];
      if (bbox) {
        exportWidth = Math.round(bbox.width);
        exportHeight = Math.round(bbox.height);
        viewBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
      }
    } else if (el.type === 'group') {
      const group = el as SvgGroupElement;
      // ★ 修复：递归序列化所有子元素（包括嵌套 group）
      const childSvgs = group.children
        .map(childId => serializeElementToSvgFragment(childId))
        .join('');

      svgCode = `<g>${childSvgs}</g>`;
      name = group.name || `组合_${group.children.length}`;
      const bbox = selectedBBoxes[elementId];
      if (bbox) {
        exportWidth = Math.round(bbox.width);
        exportHeight = Math.round(bbox.height);
        viewBox = { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
      }
    }

    if (!svgCode) return;
    downloadSvgContent(svgCode, `${name}.svg`, exportWidth, exportHeight, viewBox);
  };

  const getMousePosition = (evt: React.MouseEvent | MouseEvent) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const CTM = svgRef.current.getScreenCTM();
    if (!CTM) return { x: 0, y: 0 };
    return {
      x: (evt.clientX - CTM.e) / CTM.a,
      y: (evt.clientY - CTM.f) / CTM.d
    };
  };

  /**
   * ★ 需求1: 双击行为分流
   * - 可拆分 → 拆分
   * - 不可拆分 → 进入子元素编辑模式
   * - group → 不处理
   */
  const handleElementDoubleClick = (e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    const element = canvas?.elements.find(el => el.id === elementId);
    if (!element) return;

    // 双击 group 不处理
    if (element.type === 'group') return;

    const svgNode = element as SvgNodeElement;
    const splittable = canSplitSvgContent(svgNode.svgContent);

    if (splittable) {
      // 可拆分 → 拆分
      splitElement(elementId);
    } else {
      // 不可拆分 → 进入子元素编辑模式
      parseNodeSubElements(elementId);
      enterSubElementEditMode(elementId);
    }
  };

  /**
   * 处理子元素点击 - 从事件目标向上查找 data-sub-id
   */
  const handleSubElementClick = (e: React.MouseEvent, nodeId: string) => {
    if (!isSubElementEditMode || editingNodeId !== nodeId) {
      return;
    }

    e.stopPropagation();

    // 从点击目标向上查找带有 data-sub-id 的元素
    let target = e.target as Element | null;
    while (target && target !== e.currentTarget) {
      const subId = target.getAttribute('data-sub-id');
      if (subId) {
        selectSubElement(subId);
        return;
      }
      target = target.parentElement;
    }

    // 点击了内容区域但没有命中任何子元素，取消选中
    selectSubElement(null);
  };

  const handleStageMouseDown = (e: React.MouseEvent) => {
    const target = e.target as Element;

    // ★ 抓手模式 或 按住空格键：进入平移
    if (activeTool === 'hand' || e.button === 1) {
      // 中键或抓手工具
      e.preventDefault();
      if (isSubElementEditMode) {
        exitSubElementEditMode();
      }
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      setInteractionMode('PANNING');
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: viewport.panX,
        panY: viewport.panY,
      };
      return;
    }

    if (target.id === 'stage-bg') {
      if (isSubElementEditMode) {
        exitSubElementEditMode();
      }

      const pos = getMousePosition(e);
      setInteractionMode('SELECTING');
      setSelectionBox({
        startX: pos.x,
        startY: pos.y,
        currentX: pos.x,
        currentY: pos.y,
      });
      if (!isMultiSelectKey(e)) {
        setSelectedIds([]);
        setActiveElement(undefined);
      }
    }
  };

  /** 获取元素的 x/y（兼容 svg 和 group 类型） */
  const getElementPosition = (el: SvgCanvasElement): { x: number; y: number } => {
    if (el.type === 'svg') return { x: (el as SvgNodeElement).x, y: (el as SvgNodeElement).y };
    if (el.type === 'group') return { x: (el as SvgGroupElement).x, y: (el as SvgGroupElement).y };
    return { x: 0, y: 0 };
  };

  /**
   * ★ 修复：handleElementMouseDown
   */
  const handleElementMouseDown = (e: React.MouseEvent, elementId: string) => {
    if (isSubElementEditMode && editingNodeId === elementId) {
      return;
    }

    // ★ 修复：点击其他元素时，自动退出子元素编辑模式
    if (isSubElementEditMode && editingNodeId !== elementId) {
      exitSubElementEditMode();
    }

    e.stopPropagation();
    const pos = getMousePosition(e);

    // 如果点击的是 group 的子元素，选中整个 group
    const resolvedId = resolveGroupId(elementId);
    let newSelectedIds = [...selectedState.selectedIds];

    if (isMultiSelectKey(e)) {
      // ★ Ctrl/Shift+点击：切换选中状态
      if (newSelectedIds.includes(resolvedId)) {
        newSelectedIds = newSelectedIds.filter(id => id !== resolvedId);
      } else {
        newSelectedIds.push(resolvedId);
      }
      setSelectedIds(newSelectedIds);
      setActiveElement(newSelectedIds.length > 0 ? newSelectedIds[newSelectedIds.length - 1] : undefined);
    } else {
      if (!newSelectedIds.includes(resolvedId)) {
        newSelectedIds = [resolvedId];
        setSelectedIds(newSelectedIds);
        setActiveElement(resolvedId);
      } else if (newSelectedIds.length > 1) {
        setActiveElement(resolvedId);
      } else {
        setActiveElement(resolvedId);
      }
    }

    // 保存拖拽开始时的 BBox
    dragStartBBoxesRef.current = { ...selectedBBoxes };

    // 拖拽开始前，保存一份 canvas 快照
    const currentCanvas = useSvgStore.getState().canvas;
    dragUndoSnapshotRef.current = currentCanvas ? cloneCanvas(currentCanvas) : null;

    setInteractionMode('DRAGGING');
    dragStartRef.current = pos;

    // ★ 修复：收集所有选中元素的初始位置（包括 group）
    const finalSelectedIds = useSvgStore.getState().selectedState.selectedIds;
    const initialMap = new Map<string, { x: number; y: number }>();
    if (canvas) {
      canvas.elements.forEach(el => {
        if (finalSelectedIds.includes(el.id)) {
          const elPos = getElementPosition(el);
          initialMap.set(el.id, { x: elPos.x, y: elPos.y });
        }
      });
    }
    initialPositionsRef.current = initialMap;
  };

  /** 查找元素所属的 group（如果有的话） */
  const resolveGroupId = (elementId: string): string => {
    if (!canvas) return elementId;
    const group = canvas.elements.find(
      el => el.type === 'group' && (el as SvgGroupElement).children.includes(elementId)
    );
    return group ? group.id : elementId;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // ★ 新增：平移模式
    if (interactionMode === 'PANNING' && panStartRef.current && svgRef.current && canvas) {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      // 屏幕像素 → SVG 坐标系的换算
      const svgDx = dx / viewport.zoom;
      const svgDy = dy / viewport.zoom;
      setPan(
        panStartRef.current.panX - svgDx,
        panStartRef.current.panY - svgDy
      );
      return;
    }

    const pos = getMousePosition(e);

    if (interactionMode === 'SELECTING' && selectionBox) {
      setSelectionBox({
        ...selectionBox,
        currentX: pos.x,
        currentY: pos.y,
      });
    } else if (interactionMode === 'DRAGGING' && dragStartRef.current) {
      if (initialPositionsRef.current.size === 0) return;

      const dx = pos.x - dragStartRef.current.x;
      const dy = pos.y - dragStartRef.current.y;

      // ★ 性能修复：拖拽期间直接操作 DOM transform，不更新 store
      // 避免每帧触发 React 重渲染（复杂 SVG 的 DOMParser 解析非常昂贵）
      for (const [id, initPos] of initialPositionsRef.current.entries()) {
        const nextX = Math.round((initPos.x + dx) / SNAP_GRID) * SNAP_GRID;
        const nextY = Math.round((initPos.y + dy) / SNAP_GRID) * SNAP_GRID;

        // 查找元素的 DOM 节点并直接更新 transform
        const domEl = (
          document.getElementById(`node-group-${id}`) ??
          document.getElementById(`group-wrapper-${id}`)
        );
        if (domEl) {
          const el = canvas?.elements.find(e => e.id === id);
          const scale = (el as any)?.scale ?? 1;
          const rotation = (el as any)?.rotation ?? 0;
          domEl.setAttribute('transform', `translate(${nextX}, ${nextY}) scale(${scale}) rotate(${rotation})`);
        }
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // ★ 新增：平移结束
    if (interactionMode === 'PANNING') {
      panStartRef.current = null;
      setInteractionMode('IDLE');
      return;
    }

    if (interactionMode === 'SELECTING' && selectionBox && canvas) {
      const x1 = Math.min(selectionBox.startX, selectionBox.currentX);
      const x2 = Math.max(selectionBox.startX, selectionBox.currentX);
      const y1 = Math.min(selectionBox.startY, selectionBox.currentY);
      const y2 = Math.max(selectionBox.startY, selectionBox.currentY);

      const MIN_SELECT_SIZE = 4;
      if (Math.abs(x2 - x1) < MIN_SELECT_SIZE && Math.abs(y2 - y1) < MIN_SELECT_SIZE) {
        if (!isMultiSelectKey(e)) {
          setSelectedIds([]);
          setActiveElement(undefined);
        }
      } else {
        const groupChildIds = new Set<string>();
        canvas.elements.forEach(el => {
          if (el.type === 'group') {
            (el as SvgGroupElement).children.forEach(childId => groupChildIds.add(childId));
          }
        });

        const newlySelected: string[] = [];
        canvas.elements.forEach((el) => {
          if (groupChildIds.has(el.id)) return;

          const pos = getElementPosition(el);
          let elRect = { x: pos.x, y: pos.y, width: 50, height: 50 };

          const domEl = (
            document.getElementById(`node-content-${el.id}`) ??
            document.getElementById(`group-container-${el.id}`)
          ) as SVGGraphicsElement | null;
          if (domEl && typeof domEl.getBBox === 'function') {
            try {
              const bbox = domEl.getBBox();
              const scale = el.type === 'svg' ? ((el as SvgNodeElement).scale ?? 1)
                : el.type === 'group' ? ((el as SvgGroupElement).scale ?? 1) : 1;
              elRect = {
                x: pos.x + bbox.x * scale,
                y: pos.y + bbox.y * scale,
                width: bbox.width * scale,
                height: bbox.height * scale,
              };
            } catch { /* fallback */ }
          }

          const isIntersecting = !(
            x1 > elRect.x + elRect.width || x2 < elRect.x ||
            y1 > elRect.y + elRect.height || y2 < elRect.y
          );
          if (isIntersecting) {
            if (!newlySelected.includes(el.id)) {
              newlySelected.push(el.id);
            }
          }
        });

        const finalSelection = isMultiSelectKey(e)
          ? Array.from(new Set([...selectedState.selectedIds, ...newlySelected]))
          : newlySelected;

        setSelectedIds(finalSelection);
        setActiveElement(finalSelection.length === 1 ? finalSelection[0] : undefined);
      }
    }

    if (interactionMode === 'DRAGGING') {
      const snapshot = dragUndoSnapshotRef.current;
      dragUndoSnapshotRef.current = null;

      // ★ 性能修复：拖拽结束时，将 DOM 上的最终位置一次性提交到 store
      if (initialPositionsRef.current.size > 0 && dragStartRef.current != null) {
        const pos = getMousePosition(e);
        const dx = pos.x - dragStartRef.current.x;
        const dy = pos.y - dragStartRef.current.y;

        const updates: { id: string; partial: Partial<SvgCanvasElement> }[] = [];
        for (const [id, initPos] of initialPositionsRef.current.entries()) {
          const nextX = Math.round((initPos.x + dx) / SNAP_GRID) * SNAP_GRID;
          const nextY = Math.round((initPos.y + dy) / SNAP_GRID) * SNAP_GRID;
          updates.push({ id, partial: { x: nextX, y: nextY } as any });
        }
        // 一次性更新 store（skipUndo，由下面的 snapshot 处理 undo）
        updateElements(updates, true);

        if (snapshot) {
          const state = useSvgStore.getState();
          const newUndoStack = [...state.undoStack, snapshot];
          useSvgStore.setState({
            undoStack: newUndoStack.length > MAX_UNDO_STACK_SIZE
              ? newUndoStack.slice(newUndoStack.length - MAX_UNDO_STACK_SIZE)
              : newUndoStack,
            redoStack: [],
          });
        }
      }
      requestAnimationFrame(() => { updateBBoxes(); });
    }

    setInteractionMode('IDLE');
    setSelectionBox(null);
    dragStartRef.current = null;
    initialPositionsRef.current.clear();
    dragStartBBoxesRef.current = {};
  };

  // ★ 滚轮事件处理
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    const hasSelectedElements = selectedState.selectedIds.length > 0;

    if (hasSelectedElements) {
      let target = e.target as Element | null;
      let isOnSelected = false;
      while (target && target !== svgRef.current) {
        const id = target.id;
        if (id) {
          const match = id.match(/^(?:node-group|group-wrapper)-(.+)$/);
          if (match && selectedState.selectedIds.includes(match[1])) {
            isOnSelected = true;
            break;
          }
        }
        target = target.parentElement;
      }

      if (isOnSelected) {
        const delta = e.deltaY < 0 ? 0.05 : -0.05;
        scaleSelectedElements(delta);
        return;
      }
    }

    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    const pos = getMousePosition(e as any);
    zoomAtPoint(delta, pos.x, pos.y);
  }, [selectedState.selectedIds, scaleSelectedElements, zoomAtPoint]);

  // 快捷键处理
  // ★ 新增：空格键切换抓手
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 避免在输入框中触发
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // 空格键按下 → 切换到抓手工具
      if (e.key === ' ') {
        e.preventDefault();
        setActiveTool('hand');
        return;
      }

      // Ctrl+G = 组合
      if (e.ctrlKey && !e.shiftKey && e.key === 'g') {
        e.preventDefault();
        const { selectedIds } = useSvgStore.getState().selectedState;
        if (selectedIds.length >= 2) {
          groupElements(selectedIds);
        }
        return;
      }

      // Ctrl+Shift+G = 解组
      if (e.ctrlKey && e.shiftKey && e.key === 'G') {
        e.preventDefault();
        const { selectedIds } = useSvgStore.getState().selectedState;
        if (selectedIds.length === 1) {
          const { canvas: currentCanvas } = useSvgStore.getState();
          const el = currentCanvas?.elements.find(el => el.id === selectedIds[0]);
          if (el?.type === 'group') {
            ungroupElements(el.id);
          }
        }
        return;
      }

      // Delete 键删除
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const { selectedIds } = useSvgStore.getState().selectedState;
        if (selectedIds.length > 0) {
          e.preventDefault();
          removeElements(selectedIds);
          setSelectedIds([]);
          setActiveElement(undefined);
        }
        return;
      }

      // 方向键移动选中元素
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const { selectedIds } = useSvgStore.getState().selectedState;
        const currentCanvas = useSvgStore.getState().canvas;
        if (selectedIds.length === 0 || !currentCanvas) return;

        e.preventDefault();
        const step = e.shiftKey ? ARROW_SHIFT_STEP : ARROW_STEP;
        let dx = 0;
        let dy = 0;

        switch (e.key) {
          case 'ArrowUp':    dy = -step; break;
          case 'ArrowDown':  dy = step;  break;
          case 'ArrowLeft':  dx = -step; break;
          case 'ArrowRight': dx = step;  break;
        }

        const updates: { id: string; partial: Partial<SvgCanvasElement> }[] = [];
        for (const id of selectedIds) {
          const el = currentCanvas.elements.find(e => e.id === id);
          if (!el) continue;

          if (el.type === 'svg') {
            const node = el as SvgNodeElement;
            updates.push({ id, partial: { x: node.x + dx, y: node.y + dy } as any });
          } else if (el.type === 'group') {
            const group = el as SvgGroupElement;
            updates.push({ id, partial: { x: group.x + dx, y: group.y + dy } as any });
          }
        }

        if (updates.length > 0) {
          updateElements(updates);
        }
        return;
      }

      // Escape 退出子元素编辑模式
      if (e.key === 'Escape') {
        const { isSubElementEditMode: isEditing } = useSvgStore.getState();
        if (isEditing) {
          exitSubElementEditMode();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // ★ 空格键松开 → 恢复选择工具
      if (e.key === ' ') {
        setActiveTool('select');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [groupElements, ungroupElements, removeElements, setSelectedIds, setActiveElement, updateElements, exitSubElementEditMode]);


  /** 处理 SVG 导入 */
  const handleImportSvg = useCallback((result: SvgExtractResult, name: string) => {
    if (!canvas) return;

    const element: SvgNodeElement = {
      id: `node_${Date.now()}`,
      type: 'svg',
      sourceType: 'manual',
      svgContent: result.innerContent,
      originalSvgContent: result.innerContent,
      x: Math.round(canvas.width / 2 - result.width / 2),
      y: Math.round(canvas.height / 2 - result.height / 2),
      scale: 1,
      rotation: 0,
      opacity: 1,
      name,
    };

    addElement(element);

    // 导入后自动选中新元素
    setActiveElement(element.id);
    setSelectedIds([element.id]);
  }, [canvas, addElement, setActiveElement, setSelectedIds]);

  /** 处理 SVG 导出 */
  const handleExportSvg = () => {
     const svgEl = (window as any).__AGI_SVG_REF__ as SVGSVGElement;
     if (svgEl) {
       exportSvgFile(svgEl.innerHTML);
     } else {
       alert('导出失败：无法获取画布内容');
     }
  };

  useEffect(() => {
    (window as any).__AGI_SVG_REF__ = svgRef.current;
  });

  // ★ 性能缓存：避免每次渲染重复 DOMParser 解析
  const svgContentCacheRef = useRef<Map<string, { key: string; result: string }>>(new Map());

  /**
   * 获取渲染用的 SVG 内容（带缓存）
   */
  const getNodeSvgContent = (node: SvgNodeElement): string => {
    // 构建缓存 key：只在影响输出的属性变化时才重新计算
    const hasSubElements = node.subElements && node.subElements.length > 0;
    const needAnimation = !!(
      node.animation &&
      node.animation.animationType &&
      node.animation.animationType !== 'none' &&
      !node.animation.isOriginalAnimation
    );
    const needEditMode = isSubElementEditMode && editingNodeId === node.id && !!node.subElements;

    // 快速路径：无 subElements、无动画、无编辑模式 → 直接返回原始内容
    if (!hasSubElements && !needAnimation && !needEditMode) {
      return node.svgContent;
    }

    // 构建缓存 key
    const cacheKey = [
      node.svgContent.length, // 用长度代替完整字符串比较
      node.id,
      hasSubElements ? JSON.stringify(node.subElements!.map(s => s.customAttributes)) : '',
      needAnimation ? JSON.stringify(node.animation) : '',
      needEditMode ? selectedSubElementId ?? '' : '',
    ].join('|');

    const cached = svgContentCacheRef.current.get(node.id);
    if (cached && cached.key === cacheKey) {
      return cached.result;
    }

    // 缓存未命中，执行计算
    let content = node.svgContent;

    if (hasSubElements) {
      const hasCustomAttributes = (elements: typeof node.subElements): boolean => {
        for (const el of elements!) {
          if (Object.keys(el.customAttributes).length > 0) return true;
          if (el.children && hasCustomAttributes(el.children)) return true;
        }
        return false;
      };
      
      if (hasCustomAttributes(node.subElements)) {
        content = rebuildSvgContent(content, node.subElements!);
      }
    }

    if (needAnimation) {
      content = injectAnimationToSvg(content, node.animation);
    }

    if (needEditMode) {
      content = prepareSvgForEditMode(content, node.subElements!, selectedSubElementId);
    }

    svgContentCacheRef.current.set(node.id, { key: cacheKey, result: content });
    return content;
  };

  /**
   * ★ 需求3: 渲染"添加到素材库"图标按钮 + 导出按钮 + 删除/解组按钮
   * 放在框选框上方
   */
  const renderSelectionToolbar = (
    elementId: string,
    elementType: 'svg' | 'group',
    bbox: DOMRect,
    scale: number
  ) => {
    const toolbarY = bbox.y - 28;
    const toolbarCenterX = bbox.x + bbox.width / 2;

    return (
      <g pointerEvents="none">
        {/* 包围盒 */}
        <rect
          x={bbox.x - 2} y={bbox.y - 2}
          width={bbox.width + 4} height={bbox.height + 4}
          fill="none"
          stroke={elementType === 'group' ? '#8b5cf6' : '#3b82f6'}
          strokeWidth={elementType === 'group' ? 2 : 1.5}
          strokeDasharray={elementType === 'group' ? '5 3' : '4 2'}
        />

        {/* 尺寸标签 */}
        <text x={bbox.x} y={bbox.y - 6} fontSize="10" fill={elementType === 'group' ? '#8b5cf6' : '#3b82f6'}>
          {elementType === 'group'
            ? `组合`
            : `${Math.round(bbox.width * scale)} × ${Math.round(bbox.height * scale)}`
          }
        </text>

        {/* ★ 顶部工具栏：添加到素材库 + 导出 SVG（两个按钮并排） */}
        {/* 添加到素材库按钮 - 左 */}
        <g
          transform={`translate(${toolbarCenterX - 14}, ${toolbarY})`}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
          onClick={(e) => handleAddToAssetLibrary(e, elementId)}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <rect x="-12" y="-10" width="24" height="20" rx="4" fill="#3b82f6" opacity="0.9" />
          {/* 书签图标 */}
          <path
            d="M-5,-5 L0,-2 L5,-5 L5,5 L0,3 L-5,5 Z"
            fill="white"
            stroke="none"
          />
          <title>添加到素材库</title>
        </g>

        {/* ★ 新增：导出 SVG 按钮 - 右 */}
        <g
          transform={`translate(${toolbarCenterX + 14}, ${toolbarY})`}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
          onClick={(e) => handleExportSelectedSvg(e, elementId)}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <rect x="-12" y="-10" width="24" height="20" rx="4" fill="#10b981" opacity="0.9" />
          {/* 导出/下载图标 */}
          <path
            d="M0,-5 L0,2 M-4,-1 L0,3 L4,-1 M-5,5 L5,5"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <title>导出 SVG</title>
        </g>

        {/* group 的 X 按钮改为解组；svg 的 X 按钮保持删除 */}
        <g
          transform={`translate(${bbox.x + bbox.width + 10}, ${bbox.y})`}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
          onClick={
            elementType === 'group'
              ? (e) => handleUngroupSelected(e, elementId)
              : handleDeleteSelected
          }
          onMouseDown={(e) => e.stopPropagation()}
        >
          <circle r="8" fill={elementType === 'group' ? '#f59e0b' : '#ef4444'} />
          <text y="3" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
            {elementType === 'group' ? '⊘' : '×'}
          </text>
          <title>{elementType === 'group' ? '解散组合' : '删除'}</title>
        </g>
      </g>
    );
  };

  /** 渲染单个 SvgNodeElement */
  const renderNodeElement = (node: SvgNodeElement, isInGroup: boolean = false) => {
    const hit = selectedState.selectedIds.includes(node.id);
    const isEditing = isSubElementEditMode && editingNodeId === node.id;
    const bbox = interactionMode === 'DRAGGING'
      ? dragStartBBoxesRef.current[node.id]
      : selectedBBoxes[node.id];

    const x = node.x ?? 0;
    const y = node.y ?? 0;
    const scale = node.scale ?? 1;
    const rotation = node.rotation ?? 0;
    const transform = `translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`;
    const svgContent = getNodeSvgContent(node);

    return (
      <g
        key={node.id}
        id={`node-group-${node.id}`}
        transform={transform}
        opacity={node.opacity}
        onMouseDown={isInGroup ? undefined : (e) => handleElementMouseDown(e, node.id)}
        onDoubleClick={(e) => handleElementDoubleClick(e, node.id)}
        style={{ cursor: isEditing ? 'default' : 'move' }}
      >
        <g
          id={`node-content-${node.id}`}
          dangerouslySetInnerHTML={{ __html: svgContent }}
          onClick={isEditing ? (e) => handleSubElementClick(e, node.id) : undefined}
          style={isEditing ? { cursor: 'pointer' } : undefined}
          className="svg-node-content"
        />

        {/* 选中包围盒 + 工具栏（仅非 group 子元素时显示） */}
        {hit && bbox && !isEditing && !isInGroup && (
          renderSelectionToolbar(node.id, 'svg', bbox, scale)
        )}

        {isEditing && bbox && (
          <g pointerEvents="none">
            <rect
              x={bbox.x - 4} y={bbox.y - 4}
              width={bbox.width + 8} height={bbox.height + 8}
              fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="6 3"
            />
            <text x={bbox.x} y={bbox.y - 8} fontSize="10" fill="#10b981">
              编辑中 - 点击选择子元素
            </text>
          </g>
        )}
      </g>
    );
  };

  /** 渲染 SvgGroupElement */
  const renderGroupElement = (group: SvgGroupElement) => {
    const hit = selectedState.selectedIds.includes(group.id);
    const bbox = interactionMode === 'DRAGGING'
      ? dragStartBBoxesRef.current[group.id]
      : selectedBBoxes[group.id];

    const x = group.x ?? 0;
    const y = group.y ?? 0;
    const scale = group.scale ?? 1;
    const rotation = group.rotation ?? 0;
    const transform = `translate(${x}, ${y}) scale(${scale}) rotate(${rotation})`;

    const childElements = canvas?.elements.filter(el => group.children.includes(el.id)) ?? [];

    return (
      <g
        key={group.id}
        id={`group-wrapper-${group.id}`}
        transform={transform}
        opacity={group.opacity}
        onMouseDown={(e) => handleElementMouseDown(e, group.id)}
        style={{ cursor: 'move' }}
      >
        <g id={`group-container-${group.id}`}>
          {childElements.map(child => {
            if (child.type === 'svg') {
              return renderNodeElement(child as SvgNodeElement, true);
            }
            if (child.type === 'group') {
              return renderGroupElement(child as SvgGroupElement);
            }
            return null;
          })}
        </g>

        {/* ★ Group 选中框 + 工具栏 (解组按钮 + 添加到素材库) */}
        {hit && bbox && (
          renderSelectionToolbar(group.id, 'group', bbox, scale)
        )}
      </g>
    );
  };

  /** 计算当前 cursor 样式 */
  const canvasCursor = useMemo(() => {
    if (activeTool === 'hand') return interactionMode === 'PANNING' ? 'grabbing' : 'grab';
    if (interactionMode === 'PANNING') return 'grabbing';
    return 'default';
  }, [activeTool, interactionMode]);

  if (!canvas) {
    return (
      <div className="ai-svg-panel ai-svg-panel-center">
        <div className="ai-svg-preview-placeholder">正在初始化画布...</div>
      </div>
    );
  }

  // 判断哪些元素是 group 的子元素（这些不独立渲染）
  const groupChildIds = new Set<string>();
  canvas.elements.forEach(el => {
    if (el.type === 'group') {
      (el as SvgGroupElement).children.forEach(childId => groupChildIds.add(childId));
    }
  });

  return (
    <div className="ai-svg-panel ai-svg-panel-center">
      <div className="ai-svg-panel-header">
        {/* ★ 左侧面板展开/收起按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onToggleLeftPanel && (
            <button
              className="ai-svg-toggle-left-btn"
              onClick={onToggleLeftPanel}
              title={leftCollapsed ? '展开 AI 对话' : '收起 AI 对话'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`ai-svg-toggle-left-icon ${leftCollapsed ? 'ai-svg-toggle-left-icon--collapsed' : ''}`}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
                <polyline points={leftCollapsed ? '14 9 17 12 14 15' : '17 9 14 12 17 15'} />
              </svg>
            </button>
          )}
          <span className="ai-svg-panel-title">设计器</span>
          {isSubElementEditMode && (
            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#3b82f6', background: '#eff6ff', padding: '2px 8px', borderRadius: '4px' }}>
              子元素编辑模式
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {/* ★ 新增：抓手工具切换按钮 */}
          <button
            className={`ai-svg-tool-btn ${activeTool === 'hand' ? 'ai-svg-tool-btn-active' : ''}`}
            onClick={() => setActiveTool(activeTool === 'hand' ? 'select' : 'hand')}
            title="抓手工具 (按住空格临时切换)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v0" />
              <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2" />
              <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" />
              <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
            </svg>
          </button>

          {/* ★ 缩放指示器 */}
          <span
            style={{ fontSize: 11, color: '#64748b', cursor: 'pointer', padding: '2px 10px', borderRadius: 4, background: '#f1f5f9' }}
            onClick={handleResetViewport}
            title="点击居中全部元素"
          >
            {Math.round(viewport.zoom * 100)}%
          </span>

          {/* ★ 需求1: 拆分按钮 - 仅选中单个可拆分 svg 节点时显示 */}
          {canSplitSelected && (
            <button
              className="ai-svg-secondary-button-light gap-1.5 mr-1"
              onClick={() => splitElement(selectedState.selectedIds[0])}
              title="将 SVG 拆分为独立子元素（也可双击元素）"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="8" height="8" rx="1"/>
                <rect x="14" y="2" width="8" height="8" rx="1"/>
                <rect x="2" y="14" width="8" height="8" rx="1"/>
                <rect x="14" y="14" width="8" height="8" rx="1"/>
              </svg>
              拆分
            </button>
          )}

          <button
            className="ai-svg-secondary-button-light gap-1.5 mr-1"
            onClick={() => {
              if (canvas.elements.length === 0) return;
              setShowClearConfirm(true);
            }}
            title="清空画布所有元素"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
            清空
          </button>

          <button
            className="ai-svg-secondary-button-light gap-1.5 mr-1"
            onClick={() => setShowImportModal(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            导入 SVG
          </button>
          <button
            className="ai-svg-secondary-button-light gap-1.5"
            onClick={handleExportSvg}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            导出 SVG
          </button>

          {/* ★ 右侧面板展开/收起按钮 */}
          {onToggleRightPanel && (
            <button
              className="ai-svg-toggle-right-btn"
              onClick={onToggleRightPanel}
              title={rightCollapsed ? '展开属性面板' : '收起属性面板'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`ai-svg-toggle-right-icon ${rightCollapsed ? 'ai-svg-toggle-right-icon--collapsed' : ''}`}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="15" y1="3" x2="15" y2="21" />
                <polyline points={rightCollapsed ? '10 15 7 12 10 9' : '7 15 10 12 7 9'} />
              </svg>
            </button>
          )}
        </div>
      </div>

      <ImportSvgModal
        visible={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportSvg}
      />

      <ConfirmModal
        visible={showClearConfirm}
        title="清空画布"
        message="确认清空画布中的所有元素？此操作不可撤销。"
        confirmText="清空"
        danger
        onConfirm={() => {
          clearCanvas();
          setShowClearConfirm(false);
        }}
        onCancel={() => setShowClearConfirm(false)}
      />

      <div ref={wrapperRef} className="ai-svg-canvas-wrapper" style={{ cursor: interactionMode === 'PANNING' ? 'grab' : 'default' }}>
        <svg
          ref={svgRef}
          className="ai-svg-canvas"
          width={canvas.width}
          height={canvas.height}
          viewBox={computedViewBox}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <rect id="stage-bg" width="100%" height="100%" fill="transparent" />

          {canvas.elements.map((element) => {
            if (groupChildIds.has(element.id)) return null;

            if (element.type === 'svg') {
              return renderNodeElement(element as SvgNodeElement);
            }
            if (element.type === 'group') {
              return renderGroupElement(element as SvgGroupElement);
            }
            return null;
          })}

          {selectionBox && (
            <rect
              x={Math.min(selectionBox.startX, selectionBox.currentX)}
              y={Math.min(selectionBox.startY, selectionBox.currentY)}
              width={Math.abs(selectionBox.currentX - selectionBox.startX)}
              height={Math.abs(selectionBox.currentY - selectionBox.startY)}
              fill="rgba(59, 130, 246, 0.1)"
              stroke="#3b82f6" strokeWidth="1" pointerEvents="none"
            />
          )}
        </svg>

        {/* ─── 右下角：全局小地图 ─── */}
        <CanvasMinimap />
      </div>
    </div>
  );
};

export default SvgCanvasDesigner;